import { LogEntry } from "../types";

/**
 * Priority order for label sorting
 */
const LABEL_PRIORITY: Record<string, number> = {
  location: 0,
  container: 1,
  workload: 2,
};

/**
 * Color pairs for labels [light theme, dark theme]
 * Light theme uses vibrant colors, dark theme uses muted/darker colors for readability
 */
const LABEL_COLOR_PAIRS: [string, string][] = [
  ["#2563eb", "#1e4a99"], // blue
  ["#059669", "#0d6b4a"], // green
  ["#d97706", "#a35d05"], // amber
  ["#7c3aed", "#5b2cb0"], // violet
  ["#db2777", "#a31d5a"], // pink
  ["#0891b2", "#066b85"], // cyan
  ["#65a30d", "#4d7a0a"], // lime
  ["#ea580c", "#b04409"], // orange
  ["#4f46e5", "#3b35ad"], // indigo
  ["#be185d", "#8f1246"], // rose
  ["#0d9488", "#0a7368"], // teal
  ["#ca8a04", "#987003"], // yellow
];

/**
 * Convert labels object to sorted array of [key, value] pairs
 */
export function processLabels(labels: Record<string, string>): [string, string][] {
  const entries = Object.entries(labels);

  return entries.sort((a, b) => {
    const priorityA = LABEL_PRIORITY[a[0]] ?? 999;
    const priorityB = LABEL_PRIORITY[b[0]] ?? 999;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return a[0].localeCompare(b[0]);
  });
}

/**
 * Extract labels that are common to all entries
 */
export function extractCommonLabels(entries: Pick<LogEntry, "labels">[]): string[] {
  if (entries.length === 0) {
    return [];
  }

  if (entries.length === 1) {
    return entries[0].labels.map(([k, v]) => `${k}=${v}`);
  }

  // Get all labels from first entry as candidates
  const firstLabels = entries[0].labels;
  const commonLabels: string[] = [];

  // Check each label against all other entries
  for (const [key, value] of firstLabels) {
    const isCommon = entries.every((entry) =>
      entry.labels.some(([k, v]) => k === key && v === value)
    );

    if (isCommon) {
      commonLabels.push(`${key}=${value}`);
    }
  }

  return commonLabels;
}

/**
 * Get labels that are unique to this entry (not in common labels)
 */
export function getUniqueLabels(
  entry: Pick<LogEntry, "labels">,
  commonLabels: string[]
): [string, string][] {
  const commonSet = new Set(commonLabels);

  return entry.labels.filter(([k, v]) => !commonSet.has(`${k}=${v}`));
}

/**
 * Check if any entries have unique labels
 */
export function hasAnyUniqueLabels(entries: Pick<LogEntry, "labels">[]): boolean {
  if (entries.length <= 1) {
    return false;
  }

  const commonLabels = extractCommonLabels(entries);

  return entries.some((entry) => getUniqueLabels(entry, commonLabels).length > 0);
}

/**
 * Simple hash function for strings
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Get a deterministic color for a label key
 * @param labelKey - The label key to get color for
 * @param isDarkTheme - Whether dark theme is active
 */
export function getLabelColor(labelKey: string, isDarkTheme = false): string {
  const hash = hashString(labelKey);
  const colorPair = LABEL_COLOR_PAIRS[hash % LABEL_COLOR_PAIRS.length];
  return isDarkTheme ? colorPair[1] : colorPair[0];
}
